import type { PorchColumnPattern } from "building-types/columns/porchColumnPattern";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { LevelHalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { Porch, TargetedPorchSettings } from "building-types/porches/porch";
import type { ColonnadePoint } from "building-types/porches/porchColonnadePoint";
import type { ExteriorMaterialOverrideWithKeyMeasures } from "building-types/porches/porchKeyMeasuresTypes";
import { PorchKeyMeasuresGeometryType } from "building-types/porches/porchKeyMeasuresTypes";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { FoundationSystemSettings, SlabSettings } from "building-types/settings/foundationSettingsTypes";
import type { PlanSettings } from "building-types/settings/planSettings";
import type { PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { StructureSettings } from "building-types/settings/structureSettings";
import { PorchTrimViewNoteType, PorchViewNoteType } from "building-types/settings/viewNotesSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { WindowUnit } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { assertIsNumber } from "common/utils/assertOfType";
import { numMin } from "common/utils/nominalNumbers";
import uid from "common/utils/uid";
import { floorAssemblyTop } from "doc/elements/floor/floorAssembly";
import { porchKeyMeasuresNew } from "doc/porch/porchKeyMeasures";
import type { MergedPropLibrary } from "doc/propTools";
import { defaultPorchTrimViewNotes } from "doc/settings/defaultViewNoteSettings";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { getArchitecturalColumnOrDefault, getColumnTable } from "doc/utils/columnDescriptorUtils";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { mergeViewNoteTables } from "doc/utils/viewNotesSettingsTools";
import type { NodeBSP } from "geom/bspTypes";
import GeomTagGroup from "geom/geomTagGroup";
import { offsetPolyLine2, polyLine2Thicken } from "geom/offset";
import { polyLine2ConcatAll, polyLine2Copy, polyLine2IsClosed, polyLine2MergeCoincidentVertices } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { levelIsAbove } from "../level/levelTools";
import { groundLevelId } from "../level/levelTools";
import { makePorchColonnade } from "./porchColonnade";
import { columnBottomWidthFromColumnSections, columnBottomWidthFromPorchSettings } from "./porchColumn";
import {
  formatPorchExteriorSegmentsAndCoordinateColumnPositions,
  intersectDoorProjectionRays,
  makeDoorProjectionRays,
  makeEdgeIndexToColumnPositionsTable,
  makePorchEdgeLayout,
} from "./porchEdgeLayout";
import { makePorchHeaders } from "./porchHeader";
import { makePorchRailing } from "./porchRailing";
import { exteriorMaterialOverrideWithKeyMeasuresNew, type ResolvedPorchSettingsAndDefaults } from "./porchUtils";

const BEAM_ROOF_CAST_RAY_LENGTH = 40;

function porchNew({
  id,
  group,
  colonnadePoints,
  rooms,
  columnProfiles = [],
  props = [],
  exteriorSegments = [],
  floorPerimeters = [],
  porchColonnadeSupplementalAreaOutlines: porchEdgeOutlines = [],
  roomType,
  porchSettings,
  keyMeasures,
}: Omit<
  Porch,
  | "docEntityHandle"
  | "integerId"
  | "pickHandle"
  | "entityType"
  | "props"
  | "columnCount"
  | "exteriorSegments"
  | "floorPerimeters"
> &
  Partial<Porch>): Porch {
  const instanceId = rooms[0]?.instanceId;

  return {
    ...buildingEntityBaseNew("PORCH", id),
    ...(instanceId
      ? {
          pickHandle: { type: "COMPONENT_INSTANCE", id: instanceId },
        }
      : {}),
    group,
    colonnadePoints,
    rooms,
    props,
    exteriorSegments,
    floorPerimeters,
    columnProfiles,
    porchColonnadeSupplementalAreaOutlines: porchEdgeOutlines,
    roomType,
    porchSettings,
    keyMeasures,
  };
}

interface GeneratePorchOpts {
  readonly insideRoomBSP: NodeBSP | null;
  readonly rooms: Room[]; // connected room set forming porch
  readonly levels: Table<Level>;
  readonly level: Level;
  readonly wallSystem: WallSystem;
  readonly roomIndex: RoomIndex;
  readonly roomFloorAssemblies: Table<FloorAssembly>;
  readonly unsupportedCornerAbovePts: Vec2[];
  readonly porchSettings: PorchSettingsBase;
  readonly exteriorTrimSettings: ExteriorTrimSettings;
  readonly wallSettings: WallSettings;
  readonly profileSource: ActiveProfileSource;
  readonly columnSettings: ColumnCatalogSettings;
  readonly structureSettings: StructureSettings;
  readonly propCatalog: PropAttributeTable;
  readonly mergedPropLibrary: MergedPropLibrary;
  readonly planSettings: PlanSettings;
  readonly foundationSystemSettings: FoundationSystemSettings;
  readonly roomType: RoomType;
  readonly mirror: boolean;
  readonly porchColumnPatterns: LevelHalfEdgeAssociationTable<PorchColumnPattern>;
  readonly roofs: Table<GeneratedRoofSystem>;
  readonly windowUnits: Table<WindowUnit>;
  readonly targetedPorchSettings: TargetedPorchSettings;
  readonly issues?: BuildingGenerationIssue[];
}

export async function generatePorch({
  insideRoomBSP,
  rooms,
  roomFloorAssemblies,
  levels,
  level,
  wallSystem,
  roomIndex,
  unsupportedCornerAbovePts,
  porchSettings,
  exteriorTrimSettings,
  profileSource,
  wallSettings,
  columnSettings,
  structureSettings,
  propCatalog,
  mergedPropLibrary,
  planSettings,
  foundationSystemSettings,
  roomType,
  mirror,
  porchColumnPatterns,
  roofs,
  windowUnits,
  targetedPorchSettings,
  issues,
}: GeneratePorchOpts): Promise<Porch | undefined> {
  const keyMeasures = porchKeyMeasuresNew();
  const columnTable = getColumnTable(columnSettings);
  const { wallIndex } = wallSystem;
  const levelColumnPatterns = porchColumnPatterns[level.id];

  const allExteriorSegments = makePorchEdgeLayout(
    rooms,
    wallSystem,
    roomIndex,
    porchSettings.colonnadeTerminationRules,
    levelColumnPatterns,
    targetedPorchSettings,
  );

  if (!(allExteriorSegments.length === 1)) {
    console.assert(false, "porch rooms are not connected");
  }

  const { start: startTermType, end: endTermType, segments, halfEdgeIndices } = allExteriorSegments[0];

  if (segments.length === 0) {
    return undefined;
  }

  // consider splitting porches with differing assemblies
  const floorAssemblies = rooms.map(({ id }) => roomFloorAssemblies[id]);
  const resolvedPorchSettings = resolvePorchSettingsAndDefaults(level, floorAssemblies, porchSettings);
  const { topDatum, bottomDatum, headerDepth, columnLateralOffset, columnId } = resolvedPorchSettings;

  const columnPatternEdgeIndexToColumnPositionsTable = makeEdgeIndexToColumnPositionsTable(
    halfEdgeIndices,
    levelColumnPatterns,
  );

  const exteriorSegmentArr = formatPorchExteriorSegmentsAndCoordinateColumnPositions({
    segments,
    porchSettings,
    wallSettings,
    edgeIndexToColumnPositionsTable: columnPatternEdgeIndexToColumnPositionsTable,
    halfEdgeIndices,
    wallSystem,
    wallDatumForPorchSegmentTermination: planSettings.exteriorDimensionDatum,
  });

  // generate columns
  const column = getArchitecturalColumnOrDefault(columnTable, columnId);
  const bottomSectionWidth = columnBottomWidthFromColumnSections(column?.sections);

  // create door ray perp to door surface at center of door
  const doorRays = porchSettings.doorPathColumnSpread ? makeDoorProjectionRays(rooms, wallIndex) : [];
  const colonnadeOffset = porchSettings.baseColumnOffset ? -bottomSectionWidth / 2 : 0;
  const floorPerimeterOffset = porchPerimeterOffset(
    porchSettings,
    columnSettings,
    foundationSystemSettings,
    wallSettings,
    false,
  );

  const group = new GeomTagGroup<ExteriorMaterialOverrideWithKeyMeasures>();
  const porchEdgeOutlines: PolyLine2[] = [];
  const props: Prop[] = [];
  const columnProfiles: PolyLine2[] = [];
  const colonnadePoints: ColonnadePoint[] = [];

  const groundLevel = groundLevelId(levels);
  if (!groundLevel) {
    return undefined;
  }

  if (
    porchSettings.includeRailing ||
    (levelIsAbove(level.id, groundLevel, levels) && porchSettings.upperFloorIncludeRailing)
  ) {
    makePorchRailing({
      colonnadeOffset,
      doorRays,
      structureSettings,
      exteriorSegments: exteriorSegmentArr.map((x) => x.segment),
      bottomDatum,
      group,
      porchSettings,
      unsupportedCornerAbovePts,
      level,
      levels,
      mirror,
    });
  }

  for (const segmentIndex of Object.keys(exteriorSegmentArr)) {
    const { segment, segmentHalfEdges } = exteriorSegmentArr[segmentIndex];
    const edgeIndexToColumnPositionsTable = columnPatternEdgeIndexToColumnPositionsTable[segmentIndex];
    const columnOffsetSegment = columnLateralOffset ? offsetPolyLine2(segment, columnLateralOffset) : segment;

    // project porch doors' rays to outer segment of porch
    const doorPathIntersections = porchSettings.doorPathColumnSpread
      ? intersectDoorProjectionRays(doorRays, columnOffsetSegment)
      : {};

    if (!porchSettings.removeColumns) {
      await makePorchColonnade({
        segment: columnOffsetSegment,
        columnSettings,
        propCatalog,
        mergedPropLibrary,
        structureSettings,
        unsupportedCornerAbovePts,
        doorPathIntersections,
        resolvedPorchSettings,
        startTermType,
        endTermType,
        firstColumnSectionWidth: bottomSectionWidth,
        mirror,
        edgeIndexToColumnPositionsTable,
        room: rooms[0],
        bottomDatum,
        topDatum,
        props,
        group,
        colonnadePoints,
        columnProfiles,
        keyMeasures,
        porchSettings,
        planSettings,
        halfEdgeArr: segmentHalfEdges,
      });
    }

    if (!porchSettings.removeHeader) {
      const headerOffsetSegment = porchSettings.headerLateralOffset
        ? offsetPolyLine2(segment, porchSettings.headerLateralOffset)
        : segment;
      assertIsNumber(headerDepth);
      const { porchHeaders, porchHeaderTrims } = makePorchHeaders(
        insideRoomBSP,
        headerOffsetSegment,
        roofs,
        windowUnits,
        topDatum,
        headerDepth,
        BEAM_ROOF_CAST_RAY_LENGTH,
        keyMeasures,
        porchSettings,
        exteriorTrimSettings,
        profileSource,
        issues,
      );

      const porchHeaderViewEntityDescriptor = viewEntityDescriptorNew(
        uid(),
        ViewEntityType.PORCH_HEADER,
        porchSettings.viewNotes?.[PorchViewNoteType.PORCH_HEADER],
      );

      for (const { faces, material = "trimExterior0" } of porchHeaders) {
        group.add(
          { type: "FACE_GROUP", faces },
          exteriorMaterialOverrideWithKeyMeasuresNew(
            material,
            undefined,
            PorchKeyMeasuresGeometryType.HEADER,
            porchHeaderViewEntityDescriptor,
          ),
        );
      }

      const allTrimViewNotes = mergeViewNoteTables(
        defaultPorchTrimViewNotes,
        exteriorTrimSettings.viewNotes,
        porchSettings.viewNotes,
      );
      const porchHeaderTrimViewEntityDescriptor = viewEntityDescriptorNew(
        uid(),
        ViewEntityType.PORCH_HEADER_TRIM,
        allTrimViewNotes[PorchTrimViewNoteType.PORCH_HEADER_TRIM],
      );

      for (const { brep, material = "trimExterior0" } of porchHeaderTrims) {
        group.add(
          brep,
          exteriorMaterialOverrideWithKeyMeasuresNew(
            material,
            undefined,
            PorchKeyMeasuresGeometryType.HEADER_TRIM,
            porchHeaderTrimViewEntityDescriptor,
          ),
        );
      }
    }

    // Floor trim
    if (porchSettings.foundationOffset) {
      assertIsNumber(columnLateralOffset);
      const outline = polyLine2Thicken(polyLine2Copy(segment), floorPerimeterOffset + columnLateralOffset);

      porchEdgeOutlines.push(outline);
    }
  }

  const floorPerimeters = exteriorSegmentArr.map(({ segment }) => {
    const perimeter = offsetPolyLine2(
      polyLine2MergeCoincidentVertices(segment),
      floorPerimeterOffset + (columnLateralOffset ?? 0),
    );

    // Generate return lines back to pre-offset positions except in cases where the perimeter
    // is a closed poly (e.g. gazebos or other situations where the entire building is a porch).
    return polyLine2IsClosed(segment)
      ? perimeter
      : polyLine2ConcatAll([segment.slice(0, 2), perimeter, segment.slice(-2)]);
  });

  return porchNew({
    id: uid(),
    group,
    colonnadePoints,
    rooms,
    props,
    exteriorSegments: exteriorSegmentArr.map((x) => x.segment),
    floorPerimeters,
    columnProfiles,
    porchColonnadeSupplementalAreaOutlines: porchEdgeOutlines,
    roomType,
    porchSettings,
    keyMeasures,
  });
}

function resolvePorchSettingsAndDefaults(
  level: Level,
  floorAssemblies: FloorAssembly[],
  porchSettings: PorchSettingsBase,
): ResolvedPorchSettingsAndDefaults {
  const { floorElevation: levelFloorElevation, topOfWall } = level;
  const floorElevation = floorAssemblies.reduce((a, b) => numMin(a, floorAssemblyTop(b, level)), levelFloorElevation);
  const topOfBeam = levelFloorElevation + topOfWall;
  const columnLateralOffset = porchSettings.columnLateralOffset ?? 0;
  const headerDepth = porchSettings.headerDepth ?? level.floorPlateThickness;
  const topOfHeaderHeightOffset = porchSettings.topOfHeaderHeightOffset ?? 0;
  const bottomOfBeam = topOfBeam - headerDepth + topOfHeaderHeightOffset;
  const { columnId } = porchSettings;
  const multiColumnSpread = porchSettings.multiColumnSpread ?? 16;
  const multiColumnFrontOnly = porchSettings.multiColumnFrontOnly ?? false;

  const { removeHeader } = porchSettings;

  return {
    ...porchSettings,
    columnLateralOffset,
    headerDepth,
    bottomDatum: floorElevation,
    topDatum: removeHeader ? topOfBeam : bottomOfBeam,
    columnId,
    multiColumnSpread,
    multiColumnFrontOnly,
  };
}

export function porchPerimeterOffset(
  porchSettings: PorchSettingsBase | undefined,
  columnSettings: ColumnCatalogSettings,
  slabSettings: SlabSettings | undefined,
  wallSettings: WallSettings,
  includeColumnOffset: boolean = false,
) {
  if (porchSettings?.removeHeader && porchSettings?.removeColumns) {
    return wallSettings.exteriorWallOffset + (porchSettings.foundationOffset ?? 0);
  }

  const foundationOffset = porchSettings?.foundationOffset ?? 0;
  let columnLateralOffset = includeColumnOffset ? porchSettings?.columnLateralOffset ?? 0 : 0;
  columnLateralOffset += columnBottomWidthFromPorchSettings(porchSettings, columnSettings);

  const slabOffset = slabSettings?.porchSlabOverhang ?? 0;

  // TODO - restore and migrate
  // if (foundationOffset < 0) {
  //   foundationOffset = 0;
  // }

  // if (columnLateralOffset < 0) {
  //   columnLateralOffset = 0;
  // }

  return foundationOffset + columnLateralOffset + slabOffset;
}
